// WDCE-199 step 9f: the EFTPOS payment screen's logic, with no React, DOM or fetch: next(state, event) gives the next
// state, and the helpers below say what to poll, whether Pay is offered and which PIN pad keys to show. Step 9g wires
// it to the screen and to eftposApi.js, whose results the events carry.
//
// Events:
// - { type: 'pay' }: Pay pressed (offer it only when canPay).
// - { type: 'started', result }: the start answer.
// - { type: 'polled', result }: a status answer, while waiting, in progress or unknown (see pollRequest).
// - { type: 'lookedUp', result }: a lookup answer, when the page loads or while checking.
// - { type: 'keyPressed', key } and { type: 'keyAnswered', result }: a PIN pad key press and its answer.
//
// Phases: ready, starting, waiting, inProgress, approved (nothing left to pay), partlyPaid, declined, cancelled,
// failedToStart, unknown, checking, notFound and refused.
// - A start answer that may hide a payment that started (a 5xx, a network error, an answer that isn't JSON or isn't a
//   shape the screen knows) only ever leads to checking, then a lookup: never to failedToStart, which only the server
//   decides (a 422 payment_not_started or a 503 recovery_unavailable).
// - A payment in flight is resumed, whether a 409 payment_in_progress or a lookup returns it.
// - unknown keeps polling, slowly, and offers no Pay: the payment may yet be approved.
// - Messages are the server's own where it gives one (staffMessage, or the errors message), and amounts go through
//   money.js.
// - eftposApproved (step 9g-3a): whether any of the order's payments was approved, so the order is partly paid even
//   when the payment shown is a later one that failed. From the lookup's eftposApproved and from any approved payment
//   shown. Once true it stays true, through Pay too.

import { formatCents } from './money'
import { displayLines } from './displayText'
import { MAX_AUTH_LENGTH } from './eftposApi'

export const MESSAGES = {
  starting: 'Starting the payment...',
  checking: "Checking whether the payment started. Don't take another payment yet.",
  notFound: "Couldn't tell whether it started. Check the PIN pad.",
  lookupFailed: "Couldn't check the payment. Reload the page, then check the PIN pad.",
  keyLost: "Couldn't tell whether the key press reached the PIN pad. Check the PIN pad.",
  noPinPad: 'Choose a PIN pad first.'
}

// The server's own words for each status (EftposTransactionStatusSerializer::STAFF_MESSAGES), for the start answer,
// which has no staffMessage.
const STATUS_MESSAGES = {
  pending: 'Follow the PIN pad.',
  in_progress: 'Follow the PIN pad.',
  approved: 'Payment approved.',
  declined: 'Payment declined.',
  cancelled: 'Payment cancelled.',
  failed_to_start: "The payment didn't start.",
  unknown: 'Payment result unknown. Check the PIN pad and the NAB portal before taking another payment.'
}
const IN_FLIGHT = ['pending', 'in_progress', 'unknown']
const FINAL_PHASES = { declined: 'declined', cancelled: 'cancelled', failed_to_start: 'failedToStart' }

// Milliseconds before the next status read or lookup: about once a second while the PIN pad is working, as the status
// endpoint expects; every 10 seconds while unknown, as recovery settles it in minutes; every 2 seconds while checking.
const POLL_DELAYS = { waiting: 1000, inProgress: 1000, unknown: 10000, checking: 2000 }
const PAYABLE = ['ready', 'partlyPaid', 'declined', 'cancelled', 'failedToStart', 'notFound', 'refused']
const KEYS = [
  { key: 'cancel', label: 'Cancel' },
  { key: 'ok', label: 'OK' },
  { key: 'yes', label: 'Yes' },
  { key: 'no', label: 'No' },
  { key: 'auth', label: 'Auth', textBox: true, maxLength: MAX_AUTH_LENGTH }
]

export function initialState() {
  return {
    phase: 'ready',
    payment: null,
    message: null,
    amountText: null,
    remainingText: null,
    displayLines: [],
    offeredKeys: [],
    keySending: null,
    keyMessage: null,
    // The payment shown when Pay was pressed, so a lookup can tell a new payment from it.
    lastKnownPaymentId: null,
    // Pay was pressed and no answer has yet shown what came of it.
    startUnconfirmed: false,
    eftposApproved: false
  }
}

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)

const money = (cents) => (Number.isSafeInteger(cents) ? formatCents(cents) : null)

const lost = (result) => !isObject(result) || result.networkError === true || result.json !== true ||
  !(result.status < 500)

// The answer's errors and payment, if it's the shape the payment endpoints answer with.
function read(result) {
  if (!isObject(result) || result.json !== true || !isObject(result.body) || !isObject(result.body.errors)) return null

  const payment = isObject(result.body.eftposPayment) ? result.body.eftposPayment : null
  const message = Object.keys(result.body.errors).map((key) => result.body.errors[key])
                        .find((value) => typeof value === 'string' && value !== '') || null
  return { status: result.status, errors: result.body.errors, payment, message }
}

function checking(state) {
  return { ...state, phase: 'checking', message: MESSAGES.checking, keySending: null, keyMessage: null }
}

function refused(state, message, payment) {
  return {
    ...state, phase: 'refused', message, payment: payment || state.payment, displayLines: [], offeredKeys: [],
    keySending: null, keyMessage: null, startUnconfirmed: false
  }
}

function failedToStart(state, message, payment) {
  return { ...refused(state, message, payment), phase: 'failedToStart' }
}

function phaseFor(payment, fromStatus) {
  if (payment.status === 'pending') return 'waiting'
  if (payment.status === 'in_progress') return 'inProgress'
  // A status this screen doesn't know is treated as unknown: no Pay, and keep asking.
  if (payment.status === 'unknown' || !(payment.status in STATUS_MESSAGES)) return 'unknown'
  // The start and key answers have no staffMessage or remainingCents: one status read settles a final payment.
  if (!fromStatus) return 'waiting'
  if (payment.status !== 'approved') return FINAL_PHASES[payment.status]

  return payment.remainingCents === 0 || (payment.remainingCents < 0 && !payment.remainingUnknownReason)
    ? 'approved' : 'partlyPaid'
}

function remainingText(payment, phase) {
  if (['waiting', 'inProgress', 'unknown', 'approved'].includes(phase)) return null
  if (payment.remainingUnknownReason) return payment.remainingUnknownReason

  return payment.remainingCents > 0 ? `${money(payment.remainingCents)} still to pay` : null
}

// The state showing this payment. serverMessage is the answer's own message, used when the payment has no
// staffMessage (a 202's outcome_unknown).
function showPayment(state, payment, serverMessage = null) {
  const fromStatus = 'staffMessage' in payment
  const phase = phaseFor(payment, fromStatus)
  const sameKeyPrompt = phase === 'inProgress' && state.phase === 'inProgress' &&
    state.payment !== null && state.payment.id === payment.id

  return {
    ...state,
    phase,
    payment,
    message: payment.staffMessage || serverMessage || STATUS_MESSAGES[payment.status] || STATUS_MESSAGES.unknown,
    amountText: money(payment.amountCents),
    remainingText: remainingText(payment, phase),
    displayLines: phase === 'inProgress' ? displayLines(payment.displayText) : [],
    offeredKeys: phase === 'inProgress' && Array.isArray(payment.offeredKeys) ? payment.offeredKeys : [],
    keySending: sameKeyPrompt ? state.keySending : null,
    keyMessage: sameKeyPrompt ? state.keyMessage : null,
    startUnconfirmed: false,
    eftposApproved: state.eftposApproved || payment.status === 'approved'
  }
}

function pay(state) {
  if (!PAYABLE.includes(state.phase)) return state

  return {
    ...initialState(),
    phase: 'starting',
    message: MESSAGES.starting,
    payment: state.payment,
    amountText: state.amountText,
    lastKnownPaymentId: state.payment ? state.payment.id : null,
    startUnconfirmed: true,
    eftposApproved: state.eftposApproved
  }
}

function started(state, result) {
  if (state.phase !== 'starting') return state

  const answer = read(result)
  if (answer && answer.status === 503 && answer.errors.recovery_unavailable && answer.message) {
    return failedToStart(state, answer.message, answer.payment)
  }
  if (lost(result) || !answer) return checking(state)

  const { status, errors, payment, message } = answer
  if (status === 201 || status === 202) return payment ? showPayment(state, payment, message) : checking(state)
  if (status === 409 && errors.payment_in_progress) return payment ? showPayment(state, payment) : checking(state)
  if (status === 422 && errors.payment_not_started && message) return failedToStart(state, message, payment)
  if ([400, 403, 404, 409].includes(status) && message) return refused(state, message, payment)

  return checking(state)
}

function polled(state, result) {
  if (!['waiting', 'inProgress', 'unknown'].includes(state.phase)) return state

  const answer = read(result)
  if (lost(result) || !answer) return checking(state)

  const { status, payment, message } = answer
  if (status === 200 && payment) return payment.id === state.payment.id ? showPayment(state, payment) : state
  if (status >= 400 && message) return refused(state, message, null)

  return checking(state)
}

function lookedUp(state, result) {
  if (!['ready', 'checking'].includes(state.phase)) return state
  if (lost(result)) return checking(state)

  const answer = read(result)
  if (!answer) return refused(state, MESSAGES.lookupFailed, null)

  const { status, payment, message } = answer
  if (status !== 200) return refused(state, message || MESSAGES.lookupFailed, null)

  const known = { ...state, eftposApproved: state.eftposApproved || result.body.eftposApproved === true }
  if (payment && IN_FLIGHT.includes(payment.status)) return showPayment(known, payment)
  if (known.startUnconfirmed) {
    // After a lost start answer: only a payment newer than the one shown before Pay can be what Pay started.
    if (payment && payment.id !== known.lastKnownPaymentId) return showPayment(known, payment)
    return { ...known, phase: 'notFound', message: MESSAGES.notFound, startUnconfirmed: false }
  }
  if (payment) return showPayment(known, payment)

  const owed = isObject(result.body.owed) ? result.body.owed : {}
  return {
    ...initialState(),
    amountText: money(owed.remainingCents),
    message: owed.remainingUnknownReason || null,
    eftposApproved: known.eftposApproved
  }
}

function keyPressed(state, key) {
  if (state.phase !== 'inProgress' || state.keySending || !state.offeredKeys.includes(key)) return state

  return { ...state, keySending: key, keyMessage: null }
}

// A sent key's result arrives by polling, so the payment in the answer isn't shown.
function keyAnswered(state, result) {
  if (!state.keySending) return state

  const answer = read(result)
  if (answer && answer.status === 202) return { ...state, keySending: null, keyMessage: null }

  return { ...state, keySending: null, keyMessage: (answer && answer.message) || MESSAGES.keyLost }
}

export function next(state, event) {
  switch (event.type) {
    case 'pay': return pay(state)
    case 'started': return started(state, event.result)
    case 'polled': return polled(state, event.result)
    case 'lookedUp': return lookedUp(state, event.result)
    case 'keyPressed': return keyPressed(state, event.key)
    case 'keyAnswered': return keyAnswered(state, event.result)
    default: throw new Error(`Unknown EFTPOS screen event: ${event.type}`)
  }
}

// What to ask the server next: the payment's status, a lookup, or nothing (null).
export function pollRequest(state) {
  if (['waiting', 'inProgress', 'unknown'].includes(state.phase)) return { kind: 'status', paymentId: state.payment.id }

  return state.phase === 'checking' ? { kind: 'lookup' } : null
}

export function pollDelay(state) {
  return POLL_DELAYS[state.phase] || null
}

export function canPay(state, { eftposTerminal, canPayByEftpos }) {
  return Boolean(canPayByEftpos && eftposTerminal) && PAYABLE.includes(state.phase)
}

// Why Pay isn't offered, when staff can do something about it.
export function payBlockedReason(state, { eftposTerminal, canPayByEftpos }) {
  return canPayByEftpos && !eftposTerminal ? MESSAGES.noPinPad : null
}

export function keyButtons(offeredKeys) {
  const offered = Array.isArray(offeredKeys) ? offeredKeys : []

  return KEYS.filter((button) => offered.includes(button.key))
}
